import { z } from 'zod';

import {
  AdditionalDataEntry,
  InvitationWizardT,
} from 'src/modules/Invitations/InvitationCreate/types';
import { InvitationType } from 'src/types/rest/managementPortalApi';

const isInvitationType = (value: unknown): value is InvitationType =>
  value === InvitationType.application || value === InvitationType.tenant;

export const getInvitationForFieldSchema = (
  t: InvitationWizardT,
  invitationType: InvitationType = InvitationType.application,
) =>
  z
    .object({
      id: z.string().trim().min(1),
      label: z.string().trim().min(1),
      redirectUrl: z.string().optional(),
    })
    .nullable()
    .refine((value) => value !== null, {
      message: t('invitations:error.targetRequired', {
        context: invitationType,
      }),
    });

export const getInvitationTypeFieldSchema = (t: InvitationWizardT) =>
  z.custom<InvitationType>(isInvitationType, {
    message: t('common:required'),
  });

export const getStep1Schema = (t: InvitationWizardT) =>
  z
    .object({
      invitationFor: z
        .object({
          id: z.string(),
          label: z.string(),
          redirectUrl: z.string().optional(),
        })
        .nullable(),
      invitationType: getInvitationTypeFieldSchema(t),
    })
    .superRefine(({ invitationFor, invitationType }, context) => {
      if (!isInvitationType(invitationType) || invitationFor !== null) {
        return;
      }

      context.addIssue({
        code: 'custom',
        message: t('invitations:error.targetRequired', {
          context: invitationType,
        }),
        path: ['invitationFor'],
      });
    });

export const getEmailFieldSchema = (t: InvitationWizardT) =>
  z
    .string()
    .email(t('invitations:error.emailInvalid'))
    .refine((value) => value === value.trim(), {
      message: t('invitations:error.emailInvalid'),
    });

export const getStep2Schema = (t: InvitationWizardT) =>
  z.object({
    // Optional fields stay as plain strings because this wizard stores blank
    // values as ''.
    email: getEmailFieldSchema(t),
    firstName: z.string(),
    lastName: z.string(),
    locale: z.string(),
    location: z.string(),
  });

const hasUniqueAdditionalDataKeys = (entries: AdditionalDataEntry[]) => {
  const keys = entries
    .map(({ key }) => key.trim())
    .filter((key) => key.length > 0);

  return new Set(keys).size === keys.length;
};

export const getAdditionalDataKeyFieldSchema = (t: InvitationWizardT) =>
  z.string().trim().min(1, t('invitations:error.additionalDataKeyRequired'));

export const getAdditionalDataValueFieldSchema = (t: InvitationWizardT) =>
  z.string().trim().min(1, t('invitations:error.additionalDataValueRequired'));

export const getAdditionalDataDuplicateKeySchema = (t: InvitationWizardT) =>
  z
    .array(
      z.object({
        key: z.string(),
        value: z.string(),
      }),
    )
    .refine(hasUniqueAdditionalDataKeys, {
      message: t('invitations:error.additionalDataDuplicateKey'),
    });

export const getAdditionalDataEntrySchema = (t: InvitationWizardT) =>
  z.object({
    key: getAdditionalDataKeyFieldSchema(t),
    value: getAdditionalDataValueFieldSchema(t),
  });

export const getStep3Schema = (t: InvitationWizardT) =>
  z.object({
    additionalData: z
      .array(getAdditionalDataEntrySchema(t))
      .refine(hasUniqueAdditionalDataKeys, {
        message: t('invitations:error.additionalDataDuplicateKey'),
      }),
  });

export const getConfirmationFieldSchema = (t: InvitationWizardT) =>
  z.boolean().refine(Boolean, {
    message: t('common:required'),
  });

export const getStep4Schema = (t: InvitationWizardT) =>
  z.object({
    confirmation: getConfirmationFieldSchema(t),
  });

export const step5Schema = z.object({});
